import { FiShoppingCart as ShoppingCart } from "react-icons/fi";
import BackgroundImage from "../../../../components/ui/BackgroundImage";
import { ProductType } from "../../clothing.types";
import blackBg from "../../assets/images/black-bg.webp";
import transparentBg from "../../assets/images/transparent-bg.webp";
import useCart from "../../hooks/use-cart";
import useStore from "../../hooks/use-store";
import clickSound from "../../../../assets/audio/click.wav";
import trashSound from "../../../../assets/audio/trash.wav";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";

type Props = ProductType & {
  className?: string;
  imgSrc: string;
  canEdit?: boolean;
};

const Product = ({
  className = "",
  imgSrc,
  title,
  price,
  id,
  canEdit = false,
  stock,
}: Props) => {
  const { addToCart, removeFromCart, isInCart } = useCart();
  const { setModalData } = useStore();
  const { playEffect } = useAudioEffects();

  const inCart = isInCart(id);

  const handleCardClick = () => {
    if (inCart) {
      playEffect(trashSound);
      removeFromCart(id);
    } else {
      playEffect(clickSound);
      addToCart({ id, title, price, quantity: 1 });
    }
  };

  return (
    <div
      className={`relative select-none min-h-[70px] cursor-pointer ${className}`}
      onClick={handleCardClick}
    >
      <BackgroundImage
        imageSrc={blackBg}
        imageClassName="!object-fill"
        className="w-full"
      >
        {/** Green tint overlay — visible when in cart */}
        <div
          className="absolute inset-0 pointer-events-none z-[1] transition-opacity duration-300"
          style={{
            opacity: inCart ? 1 : 0,
            background:
              "linear-gradient(145deg, rgba(158,255,35,0.05) 0%, transparent 58%)",
          }}
        />

        {/** Inner border glow — visible when in cart */}
        <div
          className="absolute inset-0 pointer-events-none z-[1] rounded-[inherit] transition-opacity duration-300"
          style={{
            opacity: inCart ? 1 : 0,
            boxShadow: "inset 0 0 0 1px rgba(158, 255, 35, 0.13)",
          }}
        />

        <div className="p-2 w-full relative z-[2]">
          {/** ── Image & Details Row ── */}
          <div className="flex items-center space-x-2">
            {/** Product thumbnail */}
            <div
              style={{
                filter: inCart
                  ? "drop-shadow(0 0 5px rgba(158,255,35,0.28))"
                  : "none",
                transform: inCart ? "scale(1.07)" : "scale(1)",
                transition: "filter 0.25s ease-out, transform 0.25s ease-out",
              }}
            >
              <BackgroundImage
                imageSrc={transparentBg}
                imageClassName="!object-fill"
                className="p-[5px]"
              >
                <img
                  src={imgSrc}
                  alt="Clothing"
                  loading="eager"
                  width={34}
                  height={27}
                />
              </BackgroundImage>
            </div>

            {/** Title & Price */}
            <div className="flex-1 min-w-0">
              <h2 className="text-white gilroy-font text-[.95rem] truncate">
                {title}
              </h2>
              <div className="rounded-sm bg-[#9EFF23]/10 py-[1px] px-3 max-w-fit mt-[2px]">
                <p className="text-[#9EFF23] text-[.9rem]">
                  $ {new Intl.NumberFormat().format(price)}
                </p>
              </div>
            </div>

            {/** In-cart badge */}
            <div className="shrink-0 mt-6">
              {inCart && (
                <div className="w-5 h-5 rounded-full bg-[#9EFF23]/15 border border-[#9EFF23]/30 flex items-center justify-center">
                  <ShoppingCart size={9} className="text-[#9EFF23]" />
                </div>
              )}
            </div>
          </div>

          {/** ── Edit Buttons (canEdit mode) ── */}
          {canEdit && (
            <div className="absolute top-2 right-2 flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setModalData({
                    product: { id, price, title, stock, imgSrc },
                  });
                }}
                title="Edit"
                className="min-w-[50px] min-h-[18px] bg-[#FFD556]/10 hover:bg-[#FFD556]/100 hover:text-black/70 transition text-[#FFD556] text-[.8rem] flex items-center justify-center rounded-sm"
              >
                EDIT
              </button>
              <button
                title="Edit Stock"
                className="min-w-[67px] min-h-[18px] bg-white/10 text-white/80 text-[.8rem] flex items-center justify-center rounded-sm"
              >
                Stock: {stock}
              </button>
            </div>
          )}
        </div>
      </BackgroundImage>
    </div>
  );
};

export default Product;
